
import { PageHeader } from "@/components/PageHeader";
import { useCalendarEvents } from "@/hooks/useCalendarEvents";
import { useCalendarState } from "@/hooks/useCalendarState";
import { CreateEventModal } from "@/components/calendar/CreateEventModal";
import { CalendarHeader } from "@/components/calendar/CalendarHeader";
import { CalendarView } from "@/components/calendar/CalendarView";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Plus, Settings } from "lucide-react";
import { CalendarSettings } from "@/components/calendar/CalendarSettings";
import { useSettings } from "@/providers/SettingsProvider";

const Calendar = () => {
	const {
		events,
		isLoading,
		refetch
	} = useCalendarEvents();
	const {
		currentDate,
		viewMode,
		selectedDate,
		showCreateModal,
		selectedSlotDate,
		setShowCreateModal,
		setViewMode,
		navigateCalendar,
		goToToday,
		handleEventClick,
		handleDateClick,
		handleEventDrop,
		handleEventCreate,
		handleEventUpdate,
		handleEventDelete,
		handleTimeSlotClick
	} = useCalendarState();
	const [showSettings, setShowSettings] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);

	// Refetch events when modal is closed (in case an event was created/edited/deleted)
	useEffect(() => {
		if (!showCreateModal) {
			refetch();
		}
	}, [showCreateModal, refetch]);

	// Wrapper for event creation that ensures refetch
	const handleEventCreateWithRefetch = async (eventData: {
		title: string;
		start: Date;
		end: Date;
		description?: string;
		color?: string;
		relations?: {
			candidates: string[];
			offers: string[];
			companies: string[];
			matches: string[];
			contacts: string[];
		};
	}) => {
		try {
			setIsUpdating(true);
			await handleEventCreate(eventData);
			await refetch();
			toast.success("Event created successfully");
		} catch (error) {
			console.error("Error creating event:", error);
			toast.error("Failed to create event");
		} finally {
			setIsUpdating(false);
		}
	};

	// Wrapper for event update that ensures refetch
	const handleEventUpdateWithRefetch = async (eventId: string, eventData: {
		title: string;
		start: Date;
		end: Date;
		description?: string;
		color?: string;
		relations?: {
			candidates: string[];
			offers: string[];
			companies: string[];
			matches: string[];
			contacts: string[];
		};
	}) => {
		try {
			setIsUpdating(true);
			await handleEventUpdate(eventId, eventData);
			await refetch();
			toast.success("Event updated successfully");
		} catch (error) {
			console.error("Error updating event:", error);
			toast.error("Failed to update event");
		} finally {
			setIsUpdating(false);
		}
	};

	// Wrapper for event delete that ensures refetch
	const handleEventDeleteWithRefetch = async (eventId: string) => {
		try {
			setIsUpdating(true);
			await handleEventDelete(eventId);
			await refetch();
			toast.success("Event deleted successfully");
		} catch (error) {
			console.error("Error deleting event:", error);
			toast.error("Failed to delete event");
		} finally {
			setIsUpdating(false);
		}
	};
	
	if (isLoading || isUpdating) {
		return <div className="h-[calc(100vh-4rem)] flex flex-col">
				<PageHeader title="Calendar" />
				<div className="flex-1 flex items-center justify-center">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
				</div>
			</div>;
	}
	
	return <div className="h-[calc(100vh-4rem)] flex flex-col">
			<PageHeader title="Calendar" />
			
			<CalendarHeader 
				currentDate={currentDate} 
				viewMode={viewMode} 
				onNavigate={navigateCalendar} 
				onViewModeChange={setViewMode} 
				onTodayClick={goToToday} 
				actions={
					<div className="flex items-center gap-2">
						<Button variant="outline" size="sm" onClick={() => setShowSettings(true)}>
							<Settings className="h-4 w-4 mr-2" />
							Settings
						</Button>
						<Button size="sm" onClick={() => setShowCreateModal(true)} className="text-base font-bold bg-zinc-500">
							<Plus className="h-4 w-4 mr-2" />
							Add Event
						</Button>
					</div>
				} 
			/>
			
			<div className="flex-1 overflow-hidden bg-white">
				<CalendarView 
					events={events} 
					viewMode={viewMode} 
					selectedDate={currentDate} 
					onEventClick={handleEventClick} 
					onDateClick={handleDateClick} 
					onEventDrop={handleEventDrop} 
					onTimeSlotClick={handleTimeSlotClick} 
					onEventUpdate={handleEventUpdateWithRefetch} 
					onEventDelete={handleEventDeleteWithRefetch} 
				/>
			</div>

			<CalendarSettings isOpen={showSettings} onClose={() => setShowSettings(false)} />

			<CreateEventModal 
				isOpen={showCreateModal} 
				onClose={() => setShowCreateModal(false)} 
				onSave={handleEventCreateWithRefetch} 
				initialDate={selectedSlotDate} 
			/>
		</div>;
};

export default Calendar;
